import { useCallback } from "react"
import { useDispatch } from "react-redux"
import { getAIDashboardWidgetData } from "store/aiDashboard"
import { CHART_TITLE_TO_WIDGET_ID, WIDGET_LAYOUT } from "../constants"

export default function useWidgetFetcher({ setWidgets, setDashboardData, setWidgetLoadingApiIds }) {
  const dispatch = useDispatch()

  const fetchWidgetWithData = useCallback((widget, params) => {
    if (!widget.id) return Promise.resolve({ widget, data: null })
    const { period, ...widgetParams } = params
    return dispatch(getAIDashboardWidgetData(widget.id, widgetParams))
      .then((res) => ({ widget, data: res }))
      .catch(() => ({ widget, data: null }))
  }, [dispatch])

  const removePlaceholder = useCallback((placeholderId) => {
    setWidgets((prev) => prev.filter((widget) => widget.id !== placeholderId))
  }, [setWidgets])

  const clearWidgetLoading = useCallback((widgetId) => {
    setWidgetLoadingApiIds((prev) => {
      const next = new Set(prev)
      next.delete(String(widgetId))
      return next
    })
  }, [setWidgetLoadingApiIds])

  const mergeDashboardData = useCallback((key, value) => {
    setDashboardData((prev) => (prev ? { ...prev, [key]: value } : { [key]: value }))
  }, [setDashboardData])

  const replacePlaceholder = useCallback((placeholderId, patch) => {
    setWidgets((prev) =>
      prev.map((widget) => (widget.id === placeholderId ? { ...widget, ...patch } : widget))
    )
  }, [setWidgets])

  const resolveWidget = useCallback((placeholderId, widgetId, data) => {
    if (!data) {
      removePlaceholder(placeholderId)
      clearWidgetLoading(widgetId)
      return
    }

    const localId = CHART_TITLE_TO_WIDGET_ID[data.chart_title]

    if (localId) {
      mergeDashboardData(localId, {
        chart_data: data.chart_data ?? null,
        group_by: data.group_by ?? null,
        chart_type: data.chart_type ?? null,
        chart_title: data.chart_title ?? null,
        description: data.description ?? null,
        value_format: data.value_format ?? null,
      })
      replacePlaceholder(placeholderId, {
        localId,
        type: null,
        colSpan: WIDGET_LAYOUT[localId]?.colSpan ?? 1,
        rowSpan: WIDGET_LAYOUT[localId]?.rowSpan ?? 1,
      })
    } else if (data.widget_id) {
      const dynamicId = `dynamic-${data.widget_id}`
      mergeDashboardData(dynamicId, data)
      replacePlaceholder(placeholderId, { localId: dynamicId, type: "api-dynamic", colSpan: 1, rowSpan: 1 })
    } else {
      removePlaceholder(placeholderId)
    }

    clearWidgetLoading(widgetId)
  }, [removePlaceholder, clearWidgetLoading, mergeDashboardData, replacePlaceholder])

  const handleWidgetFailure = useCallback((placeholderId, widgetId) => {
    removePlaceholder(placeholderId)
    clearWidgetLoading(widgetId)
  }, [removePlaceholder, clearWidgetLoading])

  const fetchOne = useCallback((widget, params) => {
    const placeholderId = `_ph_${widget.id}`
    if (!widget.id) {
      removePlaceholder(placeholderId)
      return
    }
    fetchWidgetWithData(widget, params)
      .then(({ data }) => resolveWidget(placeholderId, widget.id, data))
      .catch(() => handleWidgetFailure(placeholderId, widget.id))
  }, [fetchWidgetWithData, resolveWidget, removePlaceholder, handleWidgetFailure])

  const fetchAll = useCallback((widgetsArray, params) => {
    widgetsArray.forEach((widget) => fetchOne(widget, params))
  }, [fetchOne])

  return { fetchAll, fetchWidgetWithData }
}
